/**
 * BrandIcons - Компоненты для отображения иконок брендов
 * ========================================================
 * 
 * Переиспользуемые компоненты для отображения логотипов
 * источников трафика и платформ
 */

import React from 'react';
import { siApple, siAndroid, siFacebook, siGoogle, siUnity } from 'simple-icons';

/**
 * Только реально используемые иконки: namespace-импорт с динамическим ключом
 * убивает tree-shaking и тянет в бандл весь пакет (~5.2 МБ, 3371 иконка).
 * Добавляешь новую иконку в sourceIconMap — добавь её и сюда.
 */
const ICONS = { siApple, siAndroid, siFacebook, siGoogle, siUnity };

/**
 * Компонент для отображения SVG иконки из simple-icons
 */
export const SimpleIcon = ({ iconName, color, size = 12 }) => {
  const icon = ICONS[iconName];
  if (!icon) return null;
  
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={color || `#${icon.hex}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={icon.path} />
    </svg>
  );
};

/**
 * Кастомная иконка для AppLovin (треугольник с тремя кругами)
 */
export const AppLovinIcon = ({ color = '#5B7FDB', size = 12 }) => {
  return (
    <svg
      viewBox="0 -50 1200 1200"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="600" cy="150" r="150" fill={color} stroke={color} strokeWidth="100"/>
      <circle cx="150" cy="900" r="150" fill={color} stroke={color} strokeWidth="100"/>
      <circle cx="1050" cy="900" r="150" fill={color} stroke={color} strokeWidth="100"/>
      <path d="M 600 150 L 150 900 L 1050 900 Z" fill="none" stroke={color} strokeWidth="100" strokeLinejoin="round"/>
    </svg>
  );
};

/**
 * Кастомная иконка для Moloco (облако с буквой M)
 */
export const MolocoIcon = ({ color = '#1E88E5', size = 12 }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Облако */}
      <path d="M19.5 10c-.3-3.9-3.5-7-7.5-7-3.1 0-5.8 1.9-6.9 4.5C2.2 8.1 0 10.7 0 13.8c0 3.4 2.8 6.2 6.2 6.2h12.6c2.8 0 5.2-2.3 5.2-5.2 0-2.6-2-4.7-4.5-4.8z"/>
      {/* Буква M */}
      <path d="M8 11h1.5l1.5 3 1.5-3H14v6h-1.5v-3.5l-1.5 3h-1l-1.5-3V17H8v-6z" fill="white"/>
    </svg>
  );
};

/**
 * Кастомная иконка для ironSource (круг с буквами is)
 */
export const IronSourceIcon = ({ color = '#1C3177', size = 12 }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Круг */}
      <circle cx="12" cy="12" r="12" fill={color}/>
      {/* Буквы is */}
      <text x="12" y="16" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif">is</text>
    </svg>
  );
};

/**
 * Кастомная иконка для Mintegral (оранжевый квадрат с белой M)
 */
export const MintegralIcon = ({ color = '#FF6B35', size = 12 }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Квадрат */}
      <rect x="2" y="2" width="20" height="20" rx="2" fill={color}/>
      {/* Буква M */}
      <text x="12" y="17" fontSize="14" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif">M</text>
    </svg>
  );
};

/**
 * Получить иконку платформы
 */
export const getPlatformIcon = (platform, size = 16) => {
  if (platform === 'ios') return <SimpleIcon iconName="siApple" color="#007AFF" size={size} />;
  if (platform === 'android') return <SimpleIcon iconName="siAndroid" color="#3DDC84" size={size} />;
  return null;
};

/**
 * Получить иконку источника трафика
 */
export const getSourceIcon = (source, size = 16) => {
  // Для AppLovin используем кастомную иконку
  if (source === 'applovin_int') {
    return <AppLovinIcon color="#5B7FDB" size={size} />;
  }
  
  // Для Moloco используем кастомную иконку
  if (source === 'moloco' || source === 'moloco_int') {
    return <MolocoIcon color="#1E88E5" size={size} />;
  }
  
  // Для ironSource используем кастомную иконку
  if (source === 'ironsource' || source === 'ironsource_int' || source === 'ironSource') {
    return <IronSourceIcon color="#1C3177" size={size} />;
  }
  
  // Для Mintegral используем кастомную иконку
  if (source === 'mintegral' || source === 'mintegral_int') {
    return <MintegralIcon color="#FF6B35" size={size} />;
  }
  
  const sourceIconMap = {
    'Facebook Ads': { icon: 'siFacebook', color: '#0866FF' },
    'googleadwords_int': { icon: 'siGoogle', color: '#4285F4' },
    'unity': { icon: 'siUnity', color: '#6B7280' },
    'unityads_int': { icon: 'siUnity', color: '#6B7280' },
    'Apple Search Ads': { icon: 'siApple', color: '#FC3C44' },
    'applesearchads_int': { icon: 'siApple', color: '#FC3C44' }
  };
  
  const iconData = sourceIconMap[source];
  if (iconData) {
    return <SimpleIcon iconName={iconData.icon} color={iconData.color} size={size} />;
  }
  return null;
};

/**
 * Компонент для отображения иконок кампании (источник + платформа)
 */
export const CampaignIcons = ({ source, platform, size = 16, gap = 2 }) => {
  const sourceIcon = getSourceIcon(source, size);
  const platformIcon = getPlatformIcon(platform, size);
  
  if (!sourceIcon && !platformIcon) return null;
  
  return (
    <div className={`flex items-center gap-${gap} shrink-0`}>
      {sourceIcon && <div className="flex items-center">{sourceIcon}</div>}
      {platformIcon && <div className="flex items-center">{platformIcon}</div>}
    </div>
  );
};
